/**
 * «Hacer cotización» — el botón de la caja.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTÁ AQUÍ Y NO EN SU PROPIA PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque un presupuesto se arma exactamente igual que una venta: se buscan los
 * productos, se ponen las cantidades, se aplica la lista de precios del
 * cliente. Todo eso ya existe y funciona en la caja.
 *
 * Una pantalla aparte para cotizar sería un segundo buscador de productos, un
 * segundo carrito y un segundo cálculo de totales — que se desincronizan a la
 * primera corrección. Lo único que cambia es qué se hace con el carrito al
 * final: cobrarlo, dejarlo en espera, o escribirlo en un papel.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL PAPEL LO ENSEÑA QUIEN NOS USA, NO ESTE BOTÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Y ese fue un fallo real, de los que no dan ningún error.
 *
 * Este componente abría él mismo la ventana con la cotización recién hecha. Y
 * vive DENTRO del bloque del carrito, que solo se dibuja cuando hay líneas. Al
 * guardar la cotización se vacía el carrito → el bloque desaparece → **y se
 * lleva la ventana por delante antes de que a nadie le diera tiempo a verla**.
 *
 * El dueño lo vio así: «no que tengas que ir a cotizaciones para imprimirla,
 * eso solo son pasos de más». Tenía toda la razón: la ventana sí se abría, un
 * instante, y moría sola.
 *
 * Ahora la cotización se entrega hacia arriba con `onHecha` y la ventana la
 * dibuja `PosPage`, que no se desmonta por vaciar un carrito.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Boton } from "@/components/ui";
import { mensajeDeError } from "@/lib/api";
import { cotizaciones, negocio } from "@/lib/endpoints";
import type { Cotizacion } from "@/lib/types";
import { useCarrito } from "@/stores/carrito";
import { aviso } from "@/stores/ui";
import { SelectorDeCliente } from "@/features/pos/SelectorDeCliente";

export function BotonHacerCotizacion({
  deshabilitado,
  onHecha,
}: {
  deshabilitado?: boolean;
  /** La cotización ya guardada. Quien reciba esto tiene que enseñar el papel. */
  onHecha: (cotizacion: Cotizacion) => void;
}) {
  const qc = useQueryClient();
  const carrito = useCarrito();
  const [pidiendoCliente, setPidiendoCliente] = useState(false);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ SI EL NEGOCIO PIDE LOS DATOS SIEMPRE, TAMBIÉN AQUÍ
    ═══════════════════════════════════════════════════════════════════════════

    Lo levantó el dueño: «no me pide el cliente para hacer la cotización, y
    debería si en las opciones está que es obligatorio».

    Y en una cotización pesa MÁS que en una venta. Una venta sin cliente se
    cobra y se acabó; un presupuesto sin cliente es un papel que se entrega y
    del que después no se sabe a quién se le dio — o sea, imposible de llamar
    para preguntar si lo va a tomar. Que es justamente para lo que existe la
    pantalla de cotizaciones.

    ⚠️ «Solo al dar crédito» NO cuenta: aquí no hay crédito que dar, ni deuda
    que tenga que tener dueño. Solo «Siempre» obliga.

    Se lee de la copia que ya cargó la aplicación y no con una petición propia:
    el mostrador tiene que funcionar sin internet, y una consulta que falla no
    puede decidir si se pregunta o no.
  */
  const { data: negocioCargado } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60 * 1000,
  });
  const exigeCliente =
    ((negocioCargado?.settings as Record<string, unknown> | undefined)
      ?.pedir_datos_cliente as string) === "siempre";

  const crear = useMutation({
    /*
      ⚠️ EL CARRITO SE LEE CON `getState()`, NO DE LA COPIA DEL COMPONENTE.

      Elegir un cliente y mandar la cotización pasan en el mismo instante: React
      todavía no ha vuelto a dibujar, así que `carrito.clienteId` de aquí arriba
      seguiría diciendo «sin cliente». La cotización saldría sin el cliente que
      se acaba de elegir — justo lo que la ventana venía a evitar.

      Leer el almacén al mandar da siempre lo que hay AHORA.
    */
    mutationFn: () => {
      const c = useCarrito.getState();
      return cotizaciones.crear({
        items: c.lineas.map((l) => ({
          item_id: l.itemId,
          variant_id: l.variantId,
          qty: String(l.cantidad),
          discount: String(l.descuento),
          staff_user_id: l.staffUserId,
          opcion_ids: l.opciones?.length ? l.opciones.map((o) => o.id) : undefined,
          sin: l.sin?.length ? l.sin.map((q) => q.variant_id) : undefined,
        })),
        customer_id: c.clienteId,
        lista_precios_id: c.listaPreciosId,
        nota: c.nota || null,
        discount_total: String(c.descuentoGlobal),
        order_tax_ids: c.impuestosPedido,
      });
    },
    onSuccess: (c) => {
      onHecha(c);
      /*
        ⚠️ El carrito se vacía AQUÍ, con la cotización ya guardada en la mano.

        Vaciarlo antes de saber si el servidor la aceptó dejaría al cajero sin
        el pedido y sin el papel: habría que volver a buscar los quince
        productos con el cliente delante.
      */
      carrito.vaciar();
      void qc.invalidateQueries({ queryKey: ["cotizaciones"] });
    },
    onError: (e) => aviso.error(mensajeDeError(e)),
  });

  function alPulsar() {
    /*
      ⚠️ LAS RECARGAS NO SE COTIZAN: se cobran en el momento, con el monto que
      pida el cliente. Mandándolas, el servidor contestaba «hay que decir de
      cuánto es» — aunque la línea lo decía — y el cajero no entendía qué hacer.
    */
    const recargas = carrito.lineas.filter((l) => l.monto != null);
    if (recargas.length > 0) {
      aviso.error(
        "Las recargas no van en una cotización",
        `Se cobran en el momento. Quita ${
          recargas.length === 1 ? `«${recargas[0]!.nombre}»` : "las recargas"
        } de la cuenta para cotizar lo demás.`,
      );
      return;
    }
    if (exigeCliente && !carrito.clienteId) {
      setPidiendoCliente(true);
      return;
    }
    crear.mutate();
  }

  return (
    <>
      <Boton
        variante="secundario"
        ancho
        icono={<FileText size={16} />}
        disabled={deshabilitado}
        cargando={crear.isPending}
        onClick={alPulsar}
      >
        Hacer cotización
      </Boton>

      {/*
        ⚠️ CERRAR CON LA X NO HACE LA COTIZACIÓN.

        Es lo que pidió el dueño con todas sus letras: «si le da a la x pues no
        se hace». Y es lo único coherente — si el negocio decidió que todo
        documento lleva cliente, no puede salir uno sin él por cerrar una
        ventana. Aquí no se pierde nada: el carrito sigue entero y se vuelve a
        pulsar cuando se sepa quién es.
      */}
      <SelectorDeCliente
        abierto={pidiendoCliente}
        cerrar={() => setPidiendoCliente(false)}
        motivo="cotizacion"
        alElegir={(cliente) => {
          carrito.ponerCliente(cliente.id, cliente.name);
          setPidiendoCliente(false);
          // Elegir a alguien es decir que sí: la cotización sale enseguida, sin
          // un paso más de confirmación que solo serviría para que alguien lo
          // deje a medias. `mutationFn` lee el carrito ya con el cliente puesto.
          crear.mutate();
        }}
      />
    </>
  );
}
